/**
 * QuizConsent (Sprint 6 B6) — pre-exam consent screen.
 *
 * Flow:
 *   1. User click "Bắt đầu thi" trên QuizList → /quizzes/:id/consent
 *   2. Component fetch Quiz info (qua /api/assessment/quizzes/?course=... or
 *      qua start attempt early then read back? — em chọn start attempt ngay,
 *      grant consent xong navigate vào /take. Start idempotent nên OK)
 *   3. Show consent text (Nghị định 13/PIPA/APPI compliant)
 *   4. DeviceCheck pre-flight (fullscreen + webcam nếu required)
 *   5. Tick "Tôi đồng ý" required → button "Bắt đầu thi" enable
 *   6. Click → POST grant consent → navigate /quizzes/attempts/<id>/take
 *
 * UX: full-page card, gradient background subtle, không AppLayout sidebar
 * (focus mode trước thi).
 */

import { useEffect, useState } from "react";
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Result,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ClockCircleOutlined,
  HomeOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
  TrophyOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useStartAttempt } from "@/api/assessment";
import { useGrantConsent } from "@/api/proctoring";
import { DeviceCheck } from "@/components/DeviceCheck";
import { ExamIdentityCheck } from "@/features/identity/ExamIdentityCheck";
import { AiPolicyNotice } from "@/features/ai/AiPolicyNotice";
import { SpeakingConsentForm } from "@/components/SpeakingConsentForm";
import { IDENTITY_GATE_CODES, type IdentityGateCode, errorCode } from "@/features/identity/api";

const { Title, Paragraph, Text } = Typography;

export function QuizConsent() {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();
  const qid = quizId ? Number(quizId) : undefined;

  const [accepted, setAccepted] = useState(false);
  const [deviceOK, setDeviceOK] = useState(false);
  const [starting, setStarting] = useState(false);

  const startMut = useStartAttempt();

  // TT 83 C1-d — BE chặn vào thi bằng 403 + code (eKYC / face match) → render bước xác thực.
  const gateCode = errorCode(startMut.error) as IdentityGateCode | undefined;
  const isIdentityGate = !!gateCode && IDENTITY_GATE_CODES.includes(gateCode);

  // Start attempt ngay khi mount (idempotent — đã có IN_PROGRESS thì return cũ).
  // Cần attempt để biết proctor_session_id + webcam_required từ quiz.
  const startAttempt = () => {
    if (!qid) return;
    startMut.mutate(qid, {
      // Lỗi chặn vào thi hiển thị bằng <Result> bên dưới (đúng lý do theo `code`
      // BE trả) — KHÔNG gộp mọi 403 thành "chưa đăng ký môn" (vd ngoài giờ làm bài).
      onError: (err: unknown) => {
        if (errorCode(err) === "not_enrolled") message.error("Bạn cần đăng ký môn học trước.");
      },
    });
  };

  useEffect(() => {
    if (!qid || startMut.isPending || startMut.data) return;
    startAttempt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qid]);

  const attempt = startMut.data;
  const grantMut = useGrantConsent(attempt?.proctor_session_id ?? undefined);

  if (isIdentityGate && qid && !startMut.isPending) {
    return (
      <ExamIdentityCheck key={gateCode} quizId={qid} code={gateCode} onPassed={startAttempt} />
    );
  }

  // Plan 2026-10-03 — bài có câu Phát âm: đồng ý thu âm (consent v3) rồi bắt đầu lại.
  if (errorCode(startMut.error) === "speaking_consent_required" && !startMut.isPending) {
    return (
      <Card style={{ maxWidth: 720, margin: "24px auto" }} title="Bài có câu Phát âm">
        <SpeakingConsentForm grantLabel="Đồng ý và bắt đầu làm bài" onGranted={startAttempt} />
      </Card>
    );
  }

  if (startMut.isError && !isIdentityGate) {
    const resp = (
      startMut.error as {
        response?: {
          status?: number;
          data?: { detail?: string; code?: string; window_state?: string };
        };
      }
    )?.response;
    const data = resp?.data;
    let title = "Không bắt đầu được bài thi";
    const subTitle = data?.detail || "Đã có lỗi xảy ra. Vui lòng thử lại sau.";
    if (data?.code === "outside_window") {
      title =
        data.window_state === "not_open" ? "Chưa đến giờ làm bài" : "Đã hết thời gian làm bài";
    } else if (data?.code === "not_enrolled") {
      title = "Bạn chưa đăng ký môn học (đúng kỳ học) này";
    } else if (resp?.status === 409) {
      title = "Bạn đã hết lượt làm bài";
    }
    return (
      <Result
        status="warning"
        title={title}
        subTitle={subTitle}
        extra={<Link to="/my-courses">← Về khóa của tôi</Link>}
        data-testid="quiz-start-blocked"
      />
    );
  }

  if (startMut.isPending || !attempt) {
    return (
      <div style={{ textAlign: "center", padding: 64 }}>
        <Spin size="large" />
        <div style={{ marginTop: 12 }}>Chuẩn bị bài thi...</div>
      </div>
    );
  }

  // Bài tập / điểm danh không giám sát (2026-10-01) → bỏ qua màn đồng ý, vào làm luôn.
  if (attempt.quiz.kind && attempt.quiz.kind !== "exam") {
    navigate(`/quizzes/attempts/${attempt.id}/take`, { replace: true });
    return null;
  }

  if (startMut.error || !attempt.proctor_session_id) {
    return (
      <Result
        status="warning"
        title="Không bắt đầu được bài thi"
        subTitle="Vui lòng kiểm tra lại đăng ký môn học."
        extra={<Link to="/my-courses">← Về khóa của tôi</Link>}
      />
    );
  }

  // Nếu attempt đã grant consent từ trước (reload mid-quiz) → skip thẳng vào take
  if (attempt.consent_given_at) {
    navigate(`/quizzes/attempts/${attempt.id}/take`, { replace: true });
    return null;
  }

  const handleStart = async () => {
    setStarting(true);
    try {
      await grantMut.mutateAsync();
      navigate(`/quizzes/attempts/${attempt.id}/take`);
    } catch {
      message.error("Không gửi được consent. Vui lòng thử lại.");
      setStarting(false);
    }
  };

  const canStart = accepted && deviceOK && !starting;

  return (
    <div style={{ maxWidth: 800, margin: "0 auto" }} data-testid="quiz-consent">
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          {
            title: (
              <Link to="/">
                <HomeOutlined />
              </Link>
            ),
          },
          { title: <Link to="/my-courses">Khóa của tôi</Link> },
          { title: "Trước khi thi" },
        ]}
      />

      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              <TrophyOutlined /> {attempt.quiz.title}
            </Title>
            <Space wrap style={{ marginTop: 8 }}>
              <Tag color="blue" icon={<ClockCircleOutlined />}>
                {attempt.quiz.duration_display}
              </Tag>
              <Tag color="green">Đạt: ≥{attempt.quiz.pass_threshold_pct}%</Tag>
              {attempt.quiz.webcam_required && (
                <Tag color="orange" icon={<VideoCameraOutlined />}>
                  Bắt buộc webcam
                </Tag>
              )}
            </Space>
          </div>

          {/* TT 83 C5 — công bố chính sách dùng AI trước khi vào thi */}
          <AiPolicyNotice policy={attempt.ai_policy} />

          <Alert
            type="info"
            showIcon
            icon={<SafetyCertificateOutlined />}
            message="Điều khoản giám sát thi cử"
            description={
              <div style={{ fontSize: 14 }}>
                <Paragraph>
                  Tôi đồng ý hệ thống ghi hình <strong>webcam</strong> + chụp{" "}
                  <strong>screenshot</strong> trong suốt bài thi nhằm mục đích{" "}
                  <strong>chống gian lận thi cử</strong>. Dữ liệu được lưu trong{" "}
                  <strong>90 ngày</strong> và sẽ tự động xoá sau thời gian này. Tôi có quyền yêu cầu
                  xem hoặc xoá dữ liệu của mình bất kỳ lúc nào bằng cách liên hệ{" "}
                  <strong>dpo@savina.vn</strong>.
                </Paragraph>
                <Paragraph style={{ marginBottom: 0 }}>
                  Hệ thống sẽ phát hiện và ghi lại các hành vi: chuyển tab, thoát fullscreen, sao
                  chép/dán văn bản. Mọi vi phạm có thể dẫn đến <strong>huỷ kết quả thi</strong>.
                </Paragraph>
              </div>
            }
          />

          <DeviceCheck webcamRequired={attempt.quiz.webcam_required} onResult={setDeviceOK} />

          <Checkbox
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            data-testid="consent-checkbox"
          >
            <Text strong>Tôi đã đọc và đồng ý với các điều khoản trên.</Text>
          </Checkbox>

          <Button
            type="primary"
            size="large"
            block
            icon={<RightOutlined />}
            disabled={!canStart}
            loading={starting}
            onClick={handleStart}
            data-testid="start-exam-btn"
          >
            Bắt đầu thi ({attempt.quiz.duration_display})
          </Button>
        </Space>
      </Card>
    </div>
  );
}

export default QuizConsent;
